import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  Chip,
  Alert,
  Collapse,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
interface PdfJob {
  jobId: string;
  fileName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  downloadUrl?: string;
  error?: string;
  metadata?: {
    batchId: number;
    batchNumber: string;
    farmer: string;
    farm: string;
    selectedPlot: string;
  };
  startTime: Date;
}

interface PdfStatusNotificationProps {
  jobs: PdfJob[];
  onJobComplete: (jobId: string, downloadUrl?: string) => void;
  onJobRemove: (jobId: string) => void;
  onJobRetry: (jobId: string) => void;
}

const PdfStatusNotification: React.FC<PdfStatusNotificationProps> = ({
  jobs,
  onJobComplete: _onJobComplete,
  onJobRemove,
  onJobRetry
}) => {
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Auto-expand active jobs
    const activeJobIds = jobs
      .filter(job => job.status === 'processing' || job.status === 'pending')
      .map(job => job.jobId);

    // eslint-disable-next-line react-hooks/set-state-in-effect -- auto-expand re-applies to ACTIVE jobs on every jobs update (even ones the user collapsed), accumulated with manual toggles; not derivable at render time without changing that behavior
    setExpandedJobs(prev => new Set([...prev, ...activeJobIds]));
  }, [jobs]);

  const toggleExpanded = (jobId: string) => {
    setExpandedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return '#1976d2'; // Blue
      case 'processing':
        return '#ed6c02'; // Orange
      case 'completed':
        return '#2e7d32'; // Green
      case 'failed':
        return '#d32f2f'; // Red
      default:
        return '#757575'; // Grey
    }
  };

  const getJobStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: '#d32f2f', fontSize: 20 }} />;
      default:
        return <PdfIcon sx={{ color: getJobStatusColor(status), fontSize: 20 }} />;
    }
  };

  const formatElapsedTime = (startTime: Date) => {
    const now = new Date();
    const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);

    if (elapsed < 60) {
      return `${elapsed}s`;
    } else if (elapsed < 3600) {
      return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
    } else {
      return `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m`;
    }
  };

  const handleDownload = (job: PdfJob) => {
    if (job.downloadUrl) {
      const link = document.createElement('a');
      link.href = job.downloadUrl;
      link.download = job.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (jobs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 1300, width: 400 }}>
      {jobs.map((job) => (
        <Card
          key={job.jobId}
          sx={{
            mb: 1,
            border: `2px solid ${getJobStatusColor(job.status)}`,
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          <CardContent sx={{ pb: '8px !important' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getJobStatusIcon(job.status)}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  PDF Generation
                </Typography>
                <Chip
                  label={job.status.toUpperCase()}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    backgroundColor: getJobStatusColor(job.status),
                    color: 'white'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {job.status === 'completed' && job.downloadUrl && (
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(job)}
                    sx={{ p: 0.5 }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {job.status === 'failed' && (
                  <IconButton
                    size="small"
                    onClick={() => onJobRetry(job.jobId)}
                    sx={{ p: 0.5 }}
                  >
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                <IconButton
                  size="small"
                  onClick={() => onJobRemove(job.jobId)}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Progress Bar */}
            {(job.status === 'processing' || job.status === 'pending') && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant={job.progress !== undefined ? 'determinate' : 'indeterminate'}
                  value={job.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'action.disabledBackground',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getJobStatusColor(job.status)
                    }
                  }}
                />
                {job.progress !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {Math.round(job.progress)}% complete
                  </Typography>
                )}
              </Box>
            )}

            {/* Job Details */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {job.fileName}
              </Typography>
              {job.metadata && (
                <Typography variant="caption" color="text.secondary">
                  {job.metadata.farmer} • {job.metadata.farm} • Plot: {job.metadata.selectedPlot}
                </Typography>
              )}
            </Box>

            {/* Status Messages */}
            {job.status === 'completed' && (
              <Alert severity="success" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                PDF generated successfully! Click download to save.
              </Alert>
            )}

            {job.status === 'failed' && (
              <Alert severity="error" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                {job.error || 'PDF generation failed. Please try again.'}
              </Alert>
            )}

            {(job.status === 'processing' || job.status === 'pending') && (
              <Typography variant="caption" color="text.secondary">
                Processing... ({formatElapsedTime(job.startTime)})
              </Typography>
            )}

            {/* Expanded Details */}
            <Collapse in={expandedJobs.has(job.jobId)}>
              <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  Job ID: {job.jobId}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Started: {job.startTime.toLocaleString()}
                </Typography>
                {job.metadata && (
                  <>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Batch: {job.metadata.batchNumber} (ID: {job.metadata.batchId})
                    </Typography>
                  </>
                )}
              </Box>
            </Collapse>

            {/* Toggle Details Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                size="small"
                onClick={() => toggleExpanded(job.jobId)}
                sx={{ fontSize: '0.7rem', minHeight: 'auto', py: 0.5 }}
              >
                {expandedJobs.has(job.jobId) ? 'Hide Details' : 'Show Details'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default PdfStatusNotification;